import { Link, Outlet } from 'react-router-dom';
import {
  Navbar,
  NavbarCollapse,
  NavbarToggle,
  DarkThemeToggle
} from 'flowbite-react';
import { UserMenu } from '../components/navigation';
import { useAuth } from '../contexts/AuthContext';
import { useAppSettings, APP_SETTING_KEYS } from '../contexts/AppSettingsContext';
import { ImpersonationBanner } from '../components/impersonation/ImpersonationBanner';

export const MainLayout = () => {
  const { currentUser, logout, isImpersonating } = useAuth();
  const { getBool } = useAppSettings();
  const calendarPageEnabled = getBool(APP_SETTING_KEYS.calendarPageEnabled, false);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col transition-colors">
      {/* Impersonation Banner */}
      {isImpersonating && <ImpersonationBanner />}

      {/* Navigation */}
      <Navbar fluid className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm px-4 lg:px-6 min-h-[64px]">
        <Link to="/" className="flex items-center space-x-3">
          {/* Official AIU Logo */}
          <img
            src="/assets/images/aiu-logo.png"
            alt="AIU Logo"
            className="h-10 w-auto"
          />
          <span className="self-center whitespace-nowrap text-xl font-semibold text-gray-900 dark:text-white">
            AIU Directory
          </span>
        </Link>

        {/* Right side - Theme toggle, User menu, and mobile toggle */}
        <div className="flex md:order-2 items-center space-x-3">
          <DarkThemeToggle className="text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg p-2.5" />
          <Link
            to="/help"
            className="text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg p-2.5 transition-colors"
            aria-label="Help & Support"
            title="Help & Support"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </Link>
          {currentUser && (
            <UserMenu
              user={currentUser}
              onLogout={logout}
            />
          )}
          <NavbarToggle />
        </div>

        {/* Navigation Links */}
        <NavbarCollapse className="w-full md:w-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-center md:space-x-8 mt-4 md:mt-0 md:h-full">
            <Link
              to="/"
              className="text-gray-700 dark:text-gray-300 hover:text-aiu-blue dark:hover:text-blue-400 font-medium transition-colors py-2 md:py-3 no-underline block"
            >
              Home
            </Link>
            <Link
              to="/leas"
              className="text-gray-700 dark:text-gray-300 hover:text-aiu-blue dark:hover:text-blue-400 font-medium transition-colors py-2 md:py-3 no-underline block"
            >
              Organizations
            </Link>
            <Link
              to="/search"
              className="text-gray-700 dark:text-gray-300 hover:text-aiu-blue dark:hover:text-blue-400 font-medium transition-colors py-2 md:py-3 no-underline block"
            >
              Search Contacts
            </Link>
            <Link
              to="/map"
              className="text-gray-700 dark:text-gray-300 hover:text-aiu-blue dark:hover:text-blue-400 font-medium transition-colors py-2 md:py-3 no-underline block"
            >
              Map
            </Link>
            {calendarPageEnabled && (
              <Link
                to="/calendar"
                className="text-gray-700 dark:text-gray-300 hover:text-aiu-blue dark:hover:text-blue-400 font-medium transition-colors py-2 md:py-3 no-underline block"
              >
                Calendar
              </Link>
            )}

            {/* Admin Menu - Only show for admins */}
            {currentUser && (currentUser.role === 'superAdmin' || currentUser.role === 'organizationAdmin') && (
              <div className="relative group">
                <button className="text-gray-700 dark:text-gray-300 hover:text-aiu-blue dark:hover:text-blue-400 font-medium transition-colors py-2 md:py-3 no-underline block flex items-center">
                  Admin
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 dark:ring-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                  <div className="py-1" role="menu">
                    <Link
                      to="/admin/staff"
                      className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white no-underline"
                      role="menuitem"
                    >
                      Staff Management
                    </Link>
                    {currentUser.role === 'superAdmin' && (
                      <>
                        <Link
                          to="/admin/users"
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white no-underline"
                          role="menuitem"
                        >
                          User Management
                        </Link>
                        <Link
                          to="/admin/roles"
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white no-underline"
                          role="menuitem"
                        >
                          Role Management
                        </Link>
                        <Link
                          to="/admin/descriptors"
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white no-underline"
                          role="menuitem"
                        >
                          Descriptor Management
                        </Link>
                        <Link
                          to="/admin/activity"
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white no-underline"
                          role="menuitem"
                        >
                          Activity Monitor
                        </Link>
                        <Link
                          to="/admin/data-health"
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white no-underline"
                          role="menuitem"
                        >
                          Data Health
                        </Link>
                        <Link
                          to="/admin/logos"
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white no-underline"
                          role="menuitem"
                        >
                          Organization Logos
                        </Link>
                        <Link
                          to="/admin/app-settings"
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white no-underline"
                          role="menuitem"
                        >
                          App Settings
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </NavbarCollapse>
      </Navbar>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="mt-auto bg-gray-800 dark:bg-gray-950 p-6 text-center border-t border-gray-700 dark:border-gray-800">
        <p className="text-sm text-gray-300 dark:text-gray-400">&copy; {new Date().getFullYear()} Allegheny Intermediate Unit. All rights reserved.</p>
      </footer>
    </div>
  );
};